// src/pages/cash_management/ClinicCashCard.jsx
import { useEffect, useMemo, useRef, useState } from "react";

import { useCashBalance, useCashTransactions } from "@api/hooks";
import { Button, Card, Dropdown, Loading, NumberField, Pagination } from "@common";
import { formatMoney } from "@utils";
import TransactionHistoryTable from "./TransactionHistoryTable";
import { fmtDate, todayIso } from "./format";

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100];

const rowsOf = (data) => (Array.isArray(data) ? data : data?.results || []);

// `count` on a paged cash-transaction response is the unpaged total, so the
// pager knows how many pages exist; an unpaged array only knows its own length.
const countOf = (data, fallbackRows) =>
  Array.isArray(data) ? data.length : Number(data?.count ?? fallbackRows.length) || 0;

/**
 * Page state for one table in this card. Deliberately per-card: each clinic
 * renders its own tables, so a page number belongs to a (clinic, table) pair
 * rather than to the page as a whole.
 */
function usePageState(initialSize = 5) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialSize);
  return { page, setPage, pageSize, setPageSize, offset: (page - 1) * pageSize };
}

/**
 * Keep a page in range when the total shrinks — a refetch (or a confirm that
 * empties the pending inbox) would otherwise leave the table on an empty page.
 */
function useClampPage({ page, setPage, pageSize }, total) {
  const totalPages = Math.max(1, Math.ceil((Number(total) || 0) / pageSize));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, setPage, totalPages]);
}

function TablePager({ state, total }) {
  return (
    <div className="mt-3 rounded-md border border-palette-slate-divider bg-white">
      <Pagination
        page={state.page}
        pageSize={state.pageSize}
        total={total}
        onPageChange={state.setPage}
        onPageSizeChange={(size) => {
          state.setPage(1);
          state.setPageSize(size);
        }}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
      />
    </div>
  );
}

export default function ClinicCashCard({
  clinic,
  date,
  userId,
  isAdminOrOrg,
  balance,
  depositJes = [],
  depositJesLoading = false,
  balanceLoading = false,
  receiverOptions,
  onCreate,
  onDecide,
  onDeposit,
  busy,
  createPending,
}) {
  const [receiverId, setReceiverId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [txDate, setTxDate] = useState(todayIso);

  // The card body reports the *viewed* period (`date`), but the form spends
  // real money that is available *now* — which is also the snapshot the
  // backend validates against on create. `occurred_on` plays no part on
  // either side: it is a user-entered approximation of when the cash moved.
  // So when the page is scoped to a past period, fetch a second, current
  // snapshot and drive the "Source" picker from it. Without this, viewing
  // e.g. July hides cash received in August and the "My cash on hand" radio
  // is disabled even though the backend would accept the transaction.
  const today = todayIso();
  const needsCurrentSnapshot = date !== today;
  const currentBalanceQuery = useCashBalance(needsCurrentSnapshot ? { clinic, as_of: today } : {}, {
    enabled: needsCurrentSnapshot,
  });
  const formBalance = useMemo(() => {
    if (!needsCurrentSnapshot) return balance;
    const rows = currentBalanceQuery.data?.results || [];
    // Fall back to the page snapshot until the current one lands, so the
    // picker never flickers to a disabled state mid-fetch.
    return rows.find((r) => r.clinic === clinic) || balance;
  }, [needsCurrentSnapshot, currentBalanceQuery.data, clinic, balance]);

  // ---- Paged listings -------------------------------------------------------
  // Both of these hit OUR database, so limit/offset go all the way to Postgres:
  // the card asks for exactly the rows it draws. They live here rather than in
  // CashManagement because the page is per (clinic, table) — one merged query
  // across every selected clinic could not answer "page 2 of THIS clinic".
  const noRefreshOptions = {
    refetchInterval: false,
    refetchIntervalInBackground: false,
  };

  const inboxPage = usePageState();
  const inboxQuery = useCashTransactions(
    {
      clinic,
      status: "pending",
      ...(isAdminOrOrg ? {} : { as: "receiver" }),
      limit: inboxPage.pageSize,
      offset: inboxPage.offset,
    },
    noRefreshOptions,
  );
  const inboxRows = useMemo(() => rowsOf(inboxQuery.data), [inboxQuery.data]);
  const inboxTotal = countOf(inboxQuery.data, inboxRows);
  // `isLoading`, not `fetchStatus` — a page change must not unmount the table
  // and its pager, or the next click has nothing to land on.
  const inboxLoading = inboxQuery.isLoading;
  useClampPage(inboxPage, inboxTotal);

  const historyPage = usePageState();
  const historyQuery = useCashTransactions(
    {
      clinic,
      ...(isAdminOrOrg ? {} : { mine: "true" }),
      limit: historyPage.pageSize,
      offset: historyPage.offset,
    },
    noRefreshOptions,
  );
  const historyRows = useMemo(() => rowsOf(historyQuery.data), [historyQuery.data]);
  const historyTotal = countOf(historyQuery.data, historyRows);
  // `isLoading`, not `fetchStatus` — a page change must not unmount the table
  // and its pager, or the next click has nothing to land on.
  const historyLoading = historyQuery.isLoading;
  useClampPage(historyPage, historyTotal);

  // The deposit-JE listing is paged in the browser, not in NetSuite. The search
  // endpoint accepts limit/offset, but CashManagement issues ONE SuiteQL query
  // for every selected clinic at once and groups the rows by subsidiary — going
  // per-clinic here would multiply NetSuite round trips by the clinic count,
  // which is the known cause of site-wide slowness. The rows are already in
  // hand, so slice them.
  const depositJesPage = usePageState();
  useClampPage(depositJesPage, depositJes.length);
  const depositJesPageRows = useMemo(() => {
    const start = (depositJesPage.page - 1) * depositJesPage.pageSize;
    return depositJes.slice(start, start + depositJesPage.pageSize);
  }, [depositJes, depositJesPage.page, depositJesPage.pageSize]);

  // Personal cash on hand + clinic balance, both as of today — derived
  // helpers for the "Source" picker below.
  const onHand = formBalance ? Number(formBalance.on_hand || 0) : 0;
  const onHandPending = formBalance ? Number(formBalance.on_hand_pending || 0) : 0;
  const onHandAvailable = Math.max(0, onHand - onHandPending);
  const clinicBalance = formBalance ? Number(formBalance.balance || 0) : 0;
  const clinicPending = formBalance ? Number(formBalance.pending_out || 0) : 0;
  const clinicAvailable = Math.max(0, clinicBalance - clinicPending);

  // The clinic balance is only meaningful on/after its checkpoint date
  // (ClinicCashBalance.date_updated_balance). Selecting an earlier date yields
  // a full-scan number that excludes the manually set base, so flag it instead
  // of showing a misleading figure.
  const checkpointDate = balance?.checkpoint_date || null;
  const beforeCheckpoint = !!(checkpointDate && date && date < checkpointDate);

  // Default the source to "personal" if the user actually has on-hand cash;
  // otherwise default to the clinic safe. Re-seeds whenever the snapshot
  // changes until the user picks a source themselves — the current-balance
  // fetch often resolves after the first render, and seeding only once would
  // leave the stale "clinic" default in place.
  const [source, setSource] = useState("clinic");
  const sourceTouched = useRef(false);
  useEffect(() => {
    if (sourceTouched.current) return;
    setSource(onHandAvailable > 0 ? "personal" : "clinic");
  }, [onHandAvailable, clinic]);

  const chooseSource = (next) => {
    sourceTouched.current = true;
    setSource(next);
  };

  // If the snapshot resolves to no personal cash, "personal" would stay
  // selected on a disabled radio. Fall back to the clinic safe so the form
  // can't submit a source the backend will reject.
  useEffect(() => {
    if (source === "personal" && onHandAvailable <= 0) setSource("clinic");
  }, [source, onHandAvailable]);

  // A giver cannot send to themselves — never offer self as a receiver and
  // never accept a self-receiver submission.
  const safeReceiverOptions = useMemo(() => {
    const meId = userId != null ? String(userId) : "";
    if (!meId) return receiverOptions;
    return receiverOptions.filter((o) => String(o.value) !== meId);
  }, [receiverOptions, userId]);

  const numericAmount = Number(amount);
  const insufficientPersonal =
    source === "personal" && numericAmount > 0 && numericAmount > onHandAvailable;
  const insufficientClinic =
    source === "clinic" && numericAmount > 0 && balance != null && numericAmount > clinicAvailable;

  const canSubmit =
    !!receiverId &&
    String(receiverId) !== String(userId ?? "") &&
    numericAmount > 0 &&
    !insufficientPersonal &&
    !insufficientClinic &&
    !busy;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    const ok = await onCreate({
      clinic,
      receiver: receiverId,
      amount: Number(amount).toFixed(2),
      occurred_on: txDate || todayIso(),
      note: note || "",
      source,
    });
    if (ok) {
      setAmount("");
      setNote("");
      setReceiverId("");
      setTxDate(todayIso());
    }
  };

  return (
    <Card className="mt-6">
      {/* Header */}
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-palette-gray-primary">
            <span className="font-bold">{clinic}</span>
            <span className="ml-2 text-sm font-normal text-palette-gray-secondary">
              Cash Management
            </span>
          </h3>
          <p className="text-xs text-palette-gray-placeholder">
            {date ? <>As of {date}</> : <>As of today</>}
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-wide text-palette-gray-secondary">
            Clinic cash balance
          </div>
          <div className="text-3xl font-semibold text-palette-gray-primary">
            {balanceLoading ? (
              <Loading size="sm" text="" />
            ) : beforeCheckpoint ? (
              <span className="text-base font-medium text-palette-gray-placeholder">
                Not available
              </span>
            ) : balance ? (
              formatMoney(clinicBalance)
            ) : (
              "—"
            )}
          </div>
          {beforeCheckpoint ? (
            <div className="mt-1 max-w-[16rem] text-xs text-amber-600">
              No balance was recorded for {date}. The earliest recorded balance for this clinic is{" "}
              {checkpointDate}.
            </div>
          ) : (
            balance &&
            clinicPending > 0 && (
              <div className="mt-1 text-xs text-amber-600">
                Pending hand-off: {formatMoney(clinicPending)}
              </div>
            )
          )}
          {balance && (onHand > 0 || onHandPending > 0) && (
            <div className="mt-2 rounded-md bg-palette-indigo-soft px-2 py-1 text-xs text-palette-indigo-heading">
              <div>
                <span className="font-semibold">Your cash on hand:</span> {formatMoney(onHand)}
              </div>
              {onHandPending > 0 && (
                <div className="text-[11px] text-palette-indigo-label">
                  Pending out: {formatMoney(onHandPending)} · Available:{" "}
                  {formatMoney(onHandAvailable)}
                </div>
              )}
              {onDeposit && onHandAvailable > 0 && (
                <div className="mt-1.5">
                  <Button
                    size="sm"
                    variant="primary"
                    disabled={busy}
                    onClick={() => onDeposit(clinic, onHandAvailable)}
                  >
                    Deposit to Safe
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Give cash form */}
      <div className="mt-5 border-t border-palette-gray-subtle pt-4">
        <h4 className="text-sm font-semibold text-palette-gray-heading mb-3">Give cash</h4>
        {safeReceiverOptions.length === 0 ? (
          <p className="text-sm text-palette-gray-secondary">
            No other users are available to receive cash for this clinic.
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {/* Source picker — clinic safe vs personal cash on hand. */}
            <div>
              <label className="block text-sm font-medium text-palette-gray-heading mb-1">
                Source
              </label>
              <div className="flex flex-wrap gap-2">
                <label
                  className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                    source === "clinic"
                      ? "border-palette-indigo-label bg-palette-indigo-soft text-palette-indigo-heading"
                      : "border-palette-gray-border bg-white text-palette-gray-heading"
                  }`}
                >
                  <input
                    type="radio"
                    name={`source-${clinic}`}
                    value="clinic"
                    checked={source === "clinic"}
                    onChange={() => chooseSource("clinic")}
                    className="accent-palette-indigo-badge"
                  />
                  <span>
                    <span className="font-medium">Clinic balance</span>
                    <span className="ml-1 text-palette-gray-secondary">
                      ({formatMoney(clinicBalance)})
                    </span>
                  </span>
                </label>
                <label
                  className={`inline-flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
                    onHandAvailable <= 0
                      ? "cursor-not-allowed border-palette-gray-divider bg-palette-gray-soft text-palette-gray-placeholder"
                      : source === "personal"
                        ? "border-palette-indigo-label bg-palette-indigo-soft text-palette-indigo-heading"
                        : "border-palette-gray-border bg-white text-palette-gray-heading"
                  }`}
                >
                  <input
                    type="radio"
                    name={`source-${clinic}`}
                    value="personal"
                    checked={source === "personal"}
                    onChange={() => chooseSource("personal")}
                    disabled={onHandAvailable <= 0}
                    className="accent-palette-indigo-badge"
                  />
                  <span>
                    <span className="font-medium">My cash on hand</span>
                    <span className="ml-1 text-palette-gray-secondary">
                      ({formatMoney(onHandAvailable)})
                    </span>
                  </span>
                </label>
              </div>
              {insufficientClinic && (
                <p className="mt-1 text-xs text-palette-red-badge">
                  Amount exceeds the available clinic balance ({formatMoney(clinicAvailable)} after
                  pending hand-offs).
                </p>
              )}
              {insufficientPersonal && (
                <p className="mt-1 text-xs text-palette-red-badge">
                  Amount exceeds your available cash on hand ({formatMoney(onHandAvailable)}).
                </p>
              )}
            </div>

            <div className="flex flex-col gap-4 md:flex-row md:items-end">
              {/* Left: receiver + amount + date + note share the available width */}
              <div className="flex-1 grid grid-cols-1 gap-4 md:grid-cols-4">
                <Dropdown
                  label="Receiver"
                  value={receiverId}
                  onChange={setReceiverId}
                  options={safeReceiverOptions}
                  placeholder="Select receiver"
                  required
                />
                <NumberField
                  label="Amount"
                  value={amount}
                  onChange={setAmount}
                  placeholder="0.00"
                  money
                />
                <div>
                  <label className="block text-sm font-medium text-palette-gray-heading mb-1">
                    Transaction Date
                  </label>
                  <input
                    type="date"
                    value={txDate}
                    onChange={(e) => setTxDate(e.target.value)}
                    className="w-full rounded-lg border border-palette-gray-border bg-white px-3 py-2 text-sm
                               focus:border-palette-indigo-label focus:ring-2 focus:ring-palette-indigo-border focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-palette-gray-heading mb-1">
                    Note
                  </label>
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Optional"
                    className="w-full rounded-lg border border-palette-gray-border bg-white px-3 py-2 text-sm
                               focus:border-palette-indigo-label focus:ring-2 focus:ring-palette-indigo-border focus:outline-none"
                  />
                </div>
              </div>
              {/* Right: action button */}
              <div className="md:shrink-0">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!canSubmit}
                  className="w-full md:w-auto"
                >
                  {createPending ? "Submitting…" : "Submit"}
                </Button>
              </div>
            </div>
            <p className="text-xs text-palette-gray-secondary">
              Submits as <strong>pending</strong>. Balance only changes once the receiver confirms.
            </p>
          </form>
        )}
      </div>

      {/* Pending inbox */}
      <div className="mt-5 border-t border-palette-gray-subtle pt-4">
        <h4 className="text-sm font-semibold text-palette-gray-heading mb-3">
          {isAdminOrOrg ? "All pending transactions" : "Pending for your confirmation"}
        </h4>
        {inboxLoading ? (
          <Loading size="sm" text="Loading pending transactions..." />
        ) : inboxRows.length === 0 ? (
          <p className="text-sm text-palette-gray-secondary">
            {isAdminOrOrg ? "No pending transactions right now." : "Nothing waiting on you."}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-palette-gray-soft text-left text-xs uppercase text-palette-gray-secondary">
                <tr>
                  <th className="px-3 py-2">Transaction Date</th>
                  <th className="px-3 py-2">From</th>
                  <th className="px-3 py-2">To</th>
                  <th className="px-3 py-2">Amount</th>
                  <th className="px-3 py-2">Note</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-palette-gray-subtle">
                {inboxRows.map((r) => (
                  <tr key={r.id}>
                    <td className="px-3 py-2 text-palette-gray-badge">
                      {r.occurred_on || fmtDate(r.created_at)}
                    </td>
                    <td className="px-3 py-2">{r.giver_email}</td>
                    <td className="px-3 py-2">{r.receiver_email || "—"}</td>
                    <td className="px-3 py-2 font-mono">{formatMoney(r.amount)}</td>
                    <td className="px-3 py-2 text-palette-gray-badge">{r.note || "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-2">
                        {String(r.receiver) !== String(userId ?? "") && (
                          <span className="self-center text-xs text-palette-gray-placeholder">
                            Receiver only
                          </span>
                        )}
                        <Button
                          size="sm"
                          variant="success"
                          disabled={busy || String(r.receiver) !== String(userId ?? "")}
                          onClick={() => onDecide(r, "confirm")}
                        >
                          Confirm
                        </Button>
                        <Button
                          size="sm"
                          variant="danger"
                          disabled={busy || String(r.receiver) !== String(userId ?? "")}
                          onClick={() => onDecide(r, "reject")}
                        >
                          Reject
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TablePager state={inboxPage} total={inboxTotal} />
          </div>
        )}
      </div>

      {/* Existing Deposit-to-Safe Journal Entries in NetSuite. Admin/organizer
          only: the NetSuite search behind it is finance-only, so for anyone else
          it would always read "none found". */}
      {isAdminOrOrg && (
        <div className="mt-5 border-t border-palette-gray-subtle pt-4">
          <h4 className="text-sm font-semibold text-palette-gray-heading mb-3">
            Existing Deposit Journal Entries
          </h4>
          {depositJesLoading ? (
            <Loading size="sm" text="Loading deposit journal entries..." />
          ) : depositJes.length === 0 ? (
            <p className="text-sm text-palette-gray-secondary">
              No deposit journal entries found in NetSuite.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-palette-gray-soft text-left text-xs uppercase text-palette-gray-secondary">
                  <tr>
                    <th className="px-3 py-2">Line Created</th>
                    <th className="px-3 py-2">Transaction</th>
                    <th className="px-3 py-2">Memo</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-right">Link</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-palette-gray-subtle">
                  {depositJesPageRows.map((je, idx) => {
                    const tid = je.transaction_id ?? je.id ?? "—";
                    const link =
                      je.link ||
                      (je.transaction_id
                        ? `https://4571901.app.netsuite.com/app/accounting/transactions/transaction.nl?id=${je.transaction_id}`
                        : null);
                    return (
                      <tr key={je.transaction_id ?? je.id ?? idx}>
                        <td className="px-3 py-2 text-palette-gray-badge">
                          {je.linecreateddate || "—"}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs">{tid}</td>
                        <td className="px-3 py-2 text-palette-gray-badge">{je.memo || "—"}</td>
                        <td className="px-3 py-2 text-right font-mono">
                          {je.debitforeignamount != null ? formatMoney(je.debitforeignamount) : "—"}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {link ? (
                            <a
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md bg-palette-emerald-soft text-palette-emerald-hover border border-palette-emerald-border hover:bg-palette-emerald-muted"
                              title="Open transaction in NetSuite"
                            >
                              View Transaction
                            </a>
                          ) : (
                            <span className="text-palette-gray-placeholder">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <TablePager state={depositJesPage} total={depositJes.length} />
            </div>
          )}
        </div>
      )}

      {/* History involving me — one row per transaction; expand to see the
          full event timeline (created → confirmed / rejected / voided). */}
      <div className="mt-5 border-t border-palette-gray-subtle pt-4">
        <h4 className="text-sm font-semibold text-palette-gray-heading mb-3">
          Your transactions on this clinic
        </h4>
        {historyLoading ? (
          <Loading size="sm" text="Loading your transactions..." />
        ) : historyRows.length === 0 ? (
          <p className="text-sm text-palette-gray-secondary">No transactions yet.</p>
        ) : (
          <>
            <TransactionHistoryTable rows={historyRows} />
            <TablePager state={historyPage} total={historyTotal} />
          </>
        )}
      </div>
    </Card>
  );
}
